import { useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword as firebaseCreateUser,
  sendEmailVerification,
  confirmPasswordReset,
  verifyPasswordResetCode,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  type UserCredential
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@/firebase';
import { createUserInFirestore } from '@/utils/firebase/user';
import { persistDiagnosticOnUser } from '@/utils/onboardingDiagnosticPayload';
import { backendAnalyticsListener } from '@/services/BackendAnalyticsListener';
import { useDevStudentView } from '@/hooks/useDevStudentView';
import { logger } from '@/utils/logger';
import { authBreadcrumb } from '@/utils/authObservability';
import { trackReferralForUser } from '@/utils/referralTracking';

interface ConsentPayload {
  marketingEmailConsent: boolean;
  transactionalEmailConsent?: boolean;
  termsAcceptedAt: string;
}

export const useAuthActions = () => {
  const {
    isDevMode,
    isStudentViewActive,
    devUserData,
    mockFirebaseUser,
    activateStudentView,
    deactivateStudentView
  } = useDevStudentView();

  const sendWelcomeIfEligible = useCallback(async (uid: string, emailVerified: boolean) => {
    // `db` and `functions` are module-level Firebase singletons and remain stable across renders
    if (!emailVerified) return;
    try {
      const userDocRef = doc(db, 'users', uid);
      const snap = await getDoc(userDocRef);
      const data = snap.data() || {};
      if (!data.welcomeEmailSent) {
        const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
        await sendWelcomeEmail({});
        await updateDoc(userDocRef, { welcomeEmailSent: true });
        logger.debug('AuthActions', 'Welcome email sent after verified login');
      }
    } catch (welcomeErr) {
      logger.warn('AuthActions', 'Welcome email send skipped/failed (non-critical)', { error: welcomeErr });
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      await sendWelcomeIfEligible(userCredential.user.uid, userCredential.user.emailVerified);
    } catch (error) {
      logger.error('AuthActions', 'Sign in error', { error });
      throw error;
    }
  }, [sendWelcomeIfEligible]);

  const signInWithGoogle = useCallback(async (consent?: ConsentPayload, skipUserCreation?: boolean) => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      if (!skipUserCreation) {
        const userDocRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(userDocRef);

        if (!docSnap.exists()) {
          await createUserInFirestore(
            user.uid,
            user.email || '',
            consent,
            {
              name: user.displayName || '',
              photoURL: user.photoURL || '',
              provider: 'google'
            }
          );
          // Track affiliate referral for new user
          trackReferralForUser(user.uid).catch((refError) => {
            logger.warn('AuthActions', 'Referral tracking failed (non-critical)', { error: refError });
          });
          await persistDiagnosticOnUser({ name: user.displayName });
        }
      }

      await sendWelcomeIfEligible(user.uid, user.emailVerified);
      return userCredential;
    } catch (error) {
      logger.error('AuthActions', 'Google sign in error', { error });
      throw error;
    }
  }, [sendWelcomeIfEligible]);

  const signInWithApple = useCallback(async (consent?: ConsentPayload, skipUserCreation?: boolean) => {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      if (!skipUserCreation) {
        const userDocRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(userDocRef);

        if (!docSnap.exists()) {
          await createUserInFirestore(
            user.uid,
            user.email || '',
            consent,
            {
              name: user.displayName || '',
              photoURL: user.photoURL || '',
              provider: 'apple'
            }
          );
          // Track affiliate referral for new user
          trackReferralForUser(user.uid).catch((refError) => {
            logger.warn('AuthActions', 'Referral tracking failed (non-critical)', { error: refError });
          });
          await persistDiagnosticOnUser({ name: user.displayName });
        }
      }

      await sendWelcomeIfEligible(user.uid, user.emailVerified);
      return userCredential;
    } catch (error) {
      logger.error('AuthActions', 'Apple sign in error', { error });
      throw error;
    }
  }, [sendWelcomeIfEligible]);

  // Redirect-based social auth — the fix for in-app browsers (Gmail/Facebook/
  // Instagram webviews) where signInWithPopup is silently blocked, which was the
  // ~0% trial-completion killer. The caller (TrialSignupFlow) persists its
  // context to sessionStorage BEFORE calling start*, because the redirect
  // navigates the page away and back, destroying in-memory React state. On
  // return, completeSocialRedirect() resolves the pending sign-in.
  const buildProvider = (provider: 'google' | 'apple') => {
    if (provider === 'apple') {
      const p = new OAuthProvider('apple.com');
      p.addScope('email');
      p.addScope('name');
      return p;
    }
    const p = new GoogleAuthProvider();
    p.addScope('email');
    p.addScope('profile');
    return p;
  };

  const startSocialRedirect = useCallback(async (provider: 'google' | 'apple') => {
    // Breadcrumb BEFORE navigating away — this is the last thing that runs in
    // this page load, so if the redirect return never produces a result we can
    // still see that the redirect was started (and for whom).
    authBreadcrumb('redirect-start', { provider });
    // Marker so the return handler only breadcrumbs when a redirect was actually
    // initiated — otherwise it fires on EVERY page load (getRedirectResult returns
    // null on normal loads), which flooded Rollbar with 356 info items.
    try { sessionStorage.setItem('swtpa_redirect_initiated', '1'); } catch { /* private mode */ }
    // Navigates away; nothing after this runs until the page reloads on return.
    await signInWithRedirect(auth, buildProvider(provider));
  }, []);

  const completeSocialRedirect = useCallback(async (): Promise<UserCredential | null> => {
    // Returns the credential when the page loads after a redirect sign-in, or
    // null when there is no pending redirect. Throws on a real auth error.
    return await getRedirectResult(auth);
  }, []);

  const logOut = useCallback(async () => {
    try {
      backendAnalyticsListener.stopListening();
      await signOut(auth);

      if (isStudentViewActive) {
        deactivateStudentView();
      }
    } catch (error) {
      logger.error('AuthActions', 'Sign out error', { error });
      throw error;
    }
  }, [deactivateStudentView, isStudentViewActive]);

  const createUser = useCallback(async (email: string, password: string, consent?: ConsentPayload) => {
    try {
      const userCredential = await firebaseCreateUser(auth, email, password);
      await createUserInFirestore(userCredential.user.uid, email, consent);
      await persistDiagnosticOnUser();

      // Track affiliate referral for new user
      trackReferralForUser(userCredential.user.uid).catch((refError) => {
        logger.warn('AuthActions', 'Referral tracking failed (non-critical)', { error: refError });
      });

      let customVerificationSent = false;
      try {
        const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
        const response = await sendCustomVerificationEmail({});
        const responseData = response?.data as { sent?: boolean; success?: boolean } | undefined;

        customVerificationSent = Boolean(responseData?.sent || responseData?.success);

        if (customVerificationSent) {
          logger.debug('AuthActions', 'Custom verification email sent via SendGrid');
        } else {
          logger.warn('AuthActions', 'Custom verification email response missing success indicator, falling back to Firebase default');
        }
      } catch (emailError) {
        logger.warn('AuthActions', 'Custom verification email failed, falling back to Firebase default', { error: emailError });
      }

      if (!customVerificationSent) {
        await sendEmailVerification(userCredential.user);
      }
    } catch (error) {
      logger.error('AuthActions', 'Error creating user', { error });
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      // Branded SendGrid reset email (not Firebase's native firebaseapp.com
      // sender, which lands in spam). Firebase still owns the reset link;
      // the backend just delivers it via SendGrid. Always resolves — the
      // backend never reveals whether the address exists.
      const sendReset = httpsCallable(functions, 'sendCustomPasswordResetEmail');
      await sendReset({ email });
    } catch (error) {
      logger.error('AuthActions', 'Password reset error', { error });
      throw error;
    }
  }, []);

  const verifyResetCode = useCallback(async (oobCode: string): Promise<string> => {
    try {
      return await verifyPasswordResetCode(auth, oobCode);
    } catch (error) {
      logger.error('AuthActions', 'Error verifying reset code', { error });
      throw error;
    }
  }, []);

  const confirmPasswordResetCode = useCallback(async (oobCode: string, newPassword: string): Promise<void> => {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
    } catch (error) {
      logger.error('AuthActions', 'Error confirming password reset', { error });
      throw error;
    }
  }, []);

  return {
    actions: {
      signIn,
      signInWithGoogle,
      signInWithApple,
      startSocialRedirect,
      completeSocialRedirect,
      logOut,
      createUser,
      resetPassword,
      confirmPasswordReset: confirmPasswordResetCode,
      verifyPasswordResetCode: verifyResetCode
    },
    devSupport: {
      isDevMode,
      isStudentViewActive,
      devUserData,
      mockFirebaseUser,
      activateStudentView,
      deactivateStudentView
    }
  } as const;
};

export type AuthActionsHook = ReturnType<typeof useAuthActions>;
